import ApiService from "@/services/Api"

const VAPID_PUBLIC_KEY_ENDPOINT = "/user-devices/vapid-public-key"
const push_subscription_ENDPOINT = "/user-devices/push-subscription"

class PushNotificationSubscriberService {
	private isBrowserPushSupported (): boolean {
		return "serviceWorker" in navigator && "PushManager" in window
	}

	private decodeVapidKey (base64String: string): Uint8Array {
		const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
		const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
		const rawData = atob(base64)
		return new Uint8Array(Array.from(rawData).map((char) => char.charCodeAt(0)))
	}

	private async fetchVapidPublicKey (): Promise<Uint8Array> {
		const response = await ApiService.get<{ publicKey: string }>(VAPID_PUBLIC_KEY_ENDPOINT)
		return this.decodeVapidKey(response.data.publicKey)
	}

	private getActiveRegistration (): Promise<ServiceWorkerRegistration | undefined> {
		return navigator.serviceWorker.getRegistration()
	}

	private async getExistingSubscription (
		registration: ServiceWorkerRegistration
	): Promise<PushSubscription | null> {
		return registration.pushManager.getSubscription()
	}

	private async createSubscription (
		registration: ServiceWorkerRegistration
	): Promise<PushSubscription> {
		const applicationServerKey = await this.fetchVapidPublicKey()

		return registration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey
		})
	}

	private async saveSubscriptionToBackend (subscription: PushSubscription): Promise<void> {
		await ApiService.post(push_subscription_ENDPOINT, { subscription })
	}

	private async deleteSubscriptionFromBackend (): Promise<void> {
		await ApiService.delete(push_subscription_ENDPOINT)
	}

	async subscribe (): Promise<PushSubscription | null> {
		if (!this.isBrowserPushSupported()) return null

		const registration = await this.getActiveRegistration()
		if (!registration) return null

		const existing = await this.getExistingSubscription(registration)
		if (existing) {
			await this.saveSubscriptionToBackend(existing)
			return existing
		}

		const subscription = await this.createSubscription(registration)
		await this.saveSubscriptionToBackend(subscription)

		return subscription
	}

	async unsubscribe (): Promise<void> {
		if (!this.isBrowserPushSupported()) return

		const registration = await this.getActiveRegistration()
		if (!registration) return

		const subscription = await registration.pushManager.getSubscription()

		if (subscription) {
			await subscription.unsubscribe()
			await this.deleteSubscriptionFromBackend()
		}
	}
}

export default new PushNotificationSubscriberService()
